import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MaskLine } from "@/components/landing/primitives";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCar = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scaleCar = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacityText = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Mouse parallax
  const mx = useSpring(0, { stiffness: 60, damping: 20 });
  const my = useSpring(0, { stiffness: 60, damping: 20 });
  const onMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set((e.clientX / innerWidth - 0.5) * 40);
    my.set((e.clientY / innerHeight - 0.5) * 30);
  };

  const stats = [
    { v: "50.000+", l: t.hero.stat1 },
    { v: "< 2h", l: t.hero.stat2 },
    { v: "300+", l: t.hero.stat3 },
  ];

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A] grid-lines"
      data-testid="hero-section"
    >
      {/* Parallax car image */}
      <motion.div
        style={{ y: yCar, scale: scaleCar, x: mx, translateY: my }}
        className="pointer-events-none absolute inset-0 z-0"
      >
        <img
          src="https://images.pexels.com/photos/38570/lamborghini-car-speed-prestige-38570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Nagy teljesítményű sportautó chiptuning"
          className="h-full w-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
      </motion.div>

      {/* Red accent glow */}
      <div className="pointer-events-none absolute -right-40 top-1/4 z-0 h-[400px] w-[400px] rounded-full bg-brand-red/20 blur-[140px]" />
      <div className="pointer-events-none absolute -left-20 bottom-1/4 z-0 h-[300px] w-[300px] rounded-full bg-brand-green/10 blur-[120px]" />

      <motion.div
        style={{ opacity: opacityText }}
        className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-5 pt-28 md:px-10"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-8 inline-flex w-fit items-center gap-3 border border-brand-green/40 bg-brand-green/5 px-4 py-2"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-brand-green" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-green">
            {t.hero.badge}
          </span>
        </motion.div>

        <h1 className="font-display text-[15vw] font-black leading-[0.85] text-white sm:text-[13vw] lg:text-[9rem]">
          <MaskLine delay={0.35}>{t.hero.title1}</MaskLine>
          <MaskLine delay={0.5} className="text-brand-red">
            {t.hero.title2}
          </MaskLine>
          <MaskLine delay={0.65} className="text-stroke-white">
            {t.hero.title3}
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("configurator")}
            className="group flex items-center gap-2 bg-brand-red px-7 py-4 font-mono text-xs font-medium uppercase tracking-widest text-white transition-transform hover:-translate-y-1 hover:shadow-glow-red"
            data-testid="hero-cta-primary"
          >
            {t.hero.ctaPrimary}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => scrollTo("solutions")}
            className="flex items-center gap-2 border border-white/20 px-7 py-4 font-mono text-xs font-medium uppercase tracking-widest text-white transition-colors hover:border-brand-green hover:text-brand-green"
            data-testid="hero-cta-secondary"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-16 flex flex-wrap gap-10 border-t border-white/10 pt-8"
        >
          {stats.map((s) => (
            <div key={s.l} data-testid="hero-stat">
              <div className="font-display text-4xl font-black text-white md:text-5xl">
                {s.v}
              </div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-white/50">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="flex flex-col items-center gap-2"
        >
          <ArrowDown className="h-5 w-5 text-white/40" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
